import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAskAI } from '../../fetchHooks/useAskAI.ts';
import type { UserType } from '../../fetchHooks/useUserProfile.ts';
import { QuickCreateMyday } from '../../pages/MyDayPage/QuickCreateMyday.tsx';
import { formatDateFull } from '../../utils/utilFunctions.tsx';

const LastLogin = ({ lastSignin }: { lastSignin: UserType['lastSignin'] }) => {
  const { t, i18n } = useTranslation();
  if (!lastSignin) return <></>;
  const { loginTime, ipAddress, os, loginStatus, errorMsg, deviceType, metadata } = lastSignin;

  // Extract enhanced information from metadata
  const geolocation = metadata?.geolocation;
  const browser = metadata?.browser;

  const formatLocation = () => {
    if (!geolocation) return '';
    const parts = [];
    if (geolocation.city) parts.push(geolocation.city);
    if (geolocation.country && geolocation.country !== geolocation.city) parts.push(geolocation.country);
    return parts.length > 0 ? ` ${t('dashboard.from')} ${parts.join(', ')}` : '';
  };

  const formatSecurityInfo = () => {
    if (!geolocation) return '';
    const securityFlags = [];
    if (geolocation.isVPN) securityFlags.push('VPN');
    if (geolocation.isTor) securityFlags.push('Tor');
    if (geolocation.isProxy) securityFlags.push('Proxy');
    if (geolocation.isHosting) securityFlags.push('Hosting');

    if (securityFlags.length > 0) {
      return ` (${securityFlags.join(', ')})`;
    }
    return '';
  };

  const formatBrowserInfo = () => {
    if (!browser) return `${deviceType}/${os}`;
    return `${browser.browser} ${browser.browserVersion} on ${browser.os} ${browser.osVersion} (${deviceType})`;
  };

  const getSecurityIcon = () => {
    if (!geolocation) return '🔒';
    if (geolocation.threatLevel === 'high') return '🚨';
    if (geolocation.threatLevel === 'medium') return '⚠️';
    if (geolocation.isVPN || geolocation.isTor) return '🔐';
    return '🔒';
  };

  if (loginStatus === 'success')
    return (
      <div style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', margin: '10px 0' }}>
        <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
          {getSecurityIcon()} <strong>{t('dashboard.lastSuccessfulLogin')}</strong>{' '}
          {formatDateFull(new Date(loginTime), i18n.language)}
        </p>
        <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>
          📍 {ipAddress}
          {formatLocation()}
          {formatSecurityInfo()}
        </p>
        <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>💻 {formatBrowserInfo()}</p>
        {geolocation?.threatLevel === 'high' && (
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#dc3545' }}>
            ⚠️ {t('dashboard.highRiskConnection')}
          </p>
        )}
      </div>
    );

  return (
    <div
      style={{
        padding: '10px',
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        margin: '10px 0',
        border: '1px solid #ffeaa7',
      }}
    >
      <p style={{ margin: '0 0 5px 0', fontSize: '14px' }}>
        ❌ <strong>{t('dashboard.lastLoginFailed')}</strong> {formatDateFull(new Date(loginTime))}
      </p>
      <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>
        📍 {ipAddress}
        {formatLocation()}
        {formatSecurityInfo()}
      </p>
      <p style={{ margin: '0 0 5px 0', fontSize: '13px', color: '#666' }}>💻 {formatBrowserInfo()}</p>
      <p style={{ margin: '0', fontSize: '12px', color: '#dc3545' }}>
        <strong>{t('dashboard.error')}</strong> {errorMsg}
      </p>
    </div>
  );
};

const Dashboard = ({ user }: { user: UserType }) => {
  const { t } = useTranslation();
  const { lastSignin } = user;
  const queryClient = useQueryClient();
  // Memoize the query string to prevent infinite re-renders
  const queryString = useMemo(() => {
    const now = new Date();
    return `Write something positive and inspiring taking into account the time of year and time of day Year: ${now.getFullYear()}, Month: ${now.getMonth() + 1}, Day: ${now.getDate()} and time of day ${now.toLocaleTimeString()}. Dont answer as if I asked you a question. Write in Norwegian, and make it short and to the point. If something very interesting happened on this day, mention it. No woke statements or left leaning ideology. No woke bullshit, no praising of environmentalism or sustainability.`;
  }, []); // Empty dependency array means this only runs once

  const { data: askAI } = useAskAI(queryString);

  const handleCreateMyday = () => {
    queryClient.invalidateQueries({ queryKey: ['useMydays'] });
    queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
  };

  return (
    <div>
      {user && <QuickCreateMyday userProfile={user} onSuccess={handleCreateMyday} />}

      <h2>{t('dashboard.welcomeBack', { name: user?.firstname })}</h2>
      <LastLogin lastSignin={lastSignin} />
      <div className="centered-container">{askAI && <i>{askAI}</i>}</div>
    </div>
  );
};

export default Dashboard;
